import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  customerEngineFileTicketV1Schema,
  type BookVariantStateV1,
  type CustomerEngineEditionV1,
} from "@lifescapsule/contracts";
import { useAuthSession } from "../../../shared/AuthSession";
import { printPreparationRefusal } from "../../../shared/friendly-request-error";
import { apiRequest } from "../../../shared/api";
import {
  CustomerButton as Button,
  CustomerCheckbox as Checkbox,
} from "../../../shared/CustomerControls";
import { loadVariantState } from "../../book-variants/variant-api";
import { PrintOrderJourney } from "../../fulfillment/PrintOrderJourney";
import { sessionSubject } from "../../fulfillment/use-physical-order";
import { sendNativeStudioCommand } from "../../studio/native-studio-command";
import { loadAcceptedEdition } from "../../studio/source-history/edition-compare-api";
import { bookHref, orderHref, printHelpHref, readingPage, readingSelection } from "./print-links";
import { usePrintBook, type PrintBook } from "./print-book";
import { PrintBookSummary, PrintEmpty, PrintShell } from "./PrintShell";
import {
  exactNativeReview,
  legacyNativeReview,
  nativeEvidenceUnconfirmed,
  signedLinkExpiresAt,
} from "./native-print-review";
import {
  NO_PRINTED_COPY,
  PRINTING_NOT_OPEN,
  PRINTING_NOT_OPEN_NOTHING_TO_DO,
} from "../../../shared/printing-open";

/** The saved files this page can show: confirmed by a retained capture, or the approved proof's own. */
const reviewable: typeof exactNativeReview = (...args) =>
  exactNativeReview(...args) || legacyNativeReview(...args);

/** Family choice comes from the owned book record, never its display name or URL alone. */
export function NativePrintReviewGate({ fallback }: { fallback: ReactNode }) {
  const { bookId = "" } = useParams();
  const [query] = useSearchParams();
  const [readAttempt, setReadAttempt] = useState(0);
  const { tokens, restoringSession } = useAuthSession();
  const book = usePrintBook(bookId, tokens?.idToken, readAttempt);
  if (!tokens && !restoringSession) return fallback;
  if (restoringSession || book.state === "loading")
    return (
      <PrintShell title="Print review" back={{ to: "/app", label: "My Library" }}>
        <p role="status">Opening your book’s print review…</p>
      </PrintShell>
    );
  if (book.state === "missing")
    return (
      <PrintShell title="Print review" back={{ to: "/app", label: "My Library" }}>
        <PrintEmpty title="We couldn’t find that book.">
          <p>
            This link doesn’t match a book in your library. No other book has been opened in its
            place.
          </p>
          <Link to="/app">Return to My Library</Link>
        </PrintEmpty>
      </PrintShell>
    );
  if (book.state !== "ready")
    return (
      <PrintShell title="Print review" back={{ to: "/app", label: "My Library" }}>
        <PrintEmpty title="We couldn’t open this book.">
          <p>Your book has not changed. Try checking this link again, or return to My Library.</p>
          <Button onClick={() => setReadAttempt((value) => value + 1)}>Try again</Button>
        </PrintEmpty>
      </PrintShell>
    );
  if (!["story", "love", "recipes", "comic"].includes(book.book.templateId ?? "")) return fallback;
  // One review per account, book and edition: a renewed sign-in keeps the review, and a delivery
  // address being typed into it, on screen (D16, 2026-11-02).
  return (
    <NativeReview
      key={`${sessionSubject(tokens!.idToken)}:${bookId}:${query.get("edition") ?? "current"}`}
      book={book.book}
      token={tokens!.idToken}
    />
  );
}

function NativeReview({ book, token }: { book: PrintBook; token: string }) {
  const [unrecordedOrder, setUnrecordedOrder] = useState<string>();
  const [query] = useSearchParams();
  const page = readingPage(query.get("page")),
    requestedEdition = query.get("edition");
  const [snapshot, setSnapshot] = useState<{
    state: BookVariantStateV1;
    edition: CustomerEngineEditionV1 | null;
  }>();
  const [attempt, setAttempt] = useState(0),
    [busy, setBusy] = useState(false),
    [issue, setIssue] = useState("");
  const [opened, setOpened] = useState<string[]>([]),
    [confirmed, setConfirmed] = useState(false);
  const [tickets, setTickets] = useState<Record<string, { url: string; expiresAt: number }>>({});
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [checking, setChecking] = useState(true);
  const [preparationRequested, setPreparationRequested] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  // When the approved proof's own links (A6) were signed: an expired one is signed again, never
  // counted as opened (review P2-14).
  const [linksAt, setLinksAt] = useState(0);
  // The check follows the book's identity only: a renewed signed cover URL is a new `book` object
  // and must not re-check (and so unmount) the review or a delivery form being filled in.
  const reviewBook = useMemo(
    () => ({ id: book.id, role: book.role, templateId: book.templateId }),
    [book.id, book.role, book.templateId],
  );
  const previousSnapshot = useRef(snapshot);
  previousSnapshot.current = snapshot;
  const epoch = useRef(0),
    checkSerial = useRef(0),
    lastCheck = useRef(""),
    inFlight = useRef(false);
  const back = { to: bookHref(book.id, page, readingSelection(query)), label: "Back to your book" };
  const checkKey = `${reviewBook.id}:${attempt}:${requestedEdition ?? ""}`;
  useEffect(() => {
    const serial = ++checkSerial.current;
    const before = previousSnapshot.current;
    // A renewed sign-in checks the saved files again quietly: the review, and a delivery address
    // being typed, stay on screen unless the files themselves changed (D16, 2026-11-02).
    const quiet = Boolean(before) && lastCheck.current === checkKey;
    if (!quiet) {
      epoch.current += 1;
      setChecking(true);
      setIssue("");
    }
    void Promise.all([
      loadVariantState(reviewBook.id, token, true),
      loadAcceptedEdition(reviewBook.id, token),
    ])
      .then(([state, edition]) => {
        if (serial !== checkSerial.current) return;
        lastCheck.current = checkKey;
        const unchanged =
          before &&
          reviewable(before.state, before.edition, reviewBook, requestedEdition) &&
          reviewable(state, edition, reviewBook, requestedEdition) &&
          before.state.edition?.editionId === state.edition?.editionId &&
          before.state.proof?.proofId === state.proof?.proofId &&
          before.state.proof?.cover.checksumSha256 === state.proof?.cover.checksumSha256 &&
          before.state.proof?.interior.checksumSha256 === state.proof?.interior.checksumSha256;
        if (!unchanged) {
          if (quiet) epoch.current += 1;
          setOpened([]);
          setConfirmed(false);
          setTickets({});
          setDeliveryOpen(false);
          setPreparationRequested(false);
        }
        setSnapshot({ state, edition });
        setLinksAt(Date.now());
        setUnconfirmed(nativeEvidenceUnconfirmed(state, edition, reviewBook));
        setChecking(false);
      })
      .catch(() => {
        // A quiet check that can't be made leaves the review as it was.
        if (serial === checkSerial.current && !quiet) {
          setSnapshot(undefined);
          setChecking(false);
          setOpened([]);
          setConfirmed(false);
          setTickets({});
          setDeliveryOpen(false);
          setIssue(
            "We couldn’t check these saved files. Your book and earlier approval are unchanged.",
          );
        }
      });
    return () => {
      checkSerial.current += 1;
    };
  }, [reviewBook, token, attempt, requestedEdition, checkKey]);
  const exact = Boolean(
    !checking &&
      snapshot &&
      exactNativeReview(snapshot.state, snapshot.edition, book, requestedEdition),
  );
  // Approved before retained captures (A6): the approved proof's own files, prepared again.
  const legacy = Boolean(
    !checking &&
      snapshot &&
      !exact &&
      legacyNativeReview(snapshot.state, snapshot.edition, book, requestedEdition),
  );
  const filesHere = exact || legacy;
  const state = snapshot?.state,
    edition = snapshot?.edition,
    delivery = state?.delivery;
  const identity = `${state?.edition?.editionId}:${state?.proof?.proofId}`;
  const context = useRef("");
  context.current = `${sessionSubject(token)}:${identity}`;
  async function privateFile(assetId: "COVER" | "INTERIOR") {
    if (!exact || inFlight.current || !edition?.proofId) return;
    const active = context.current;
    const serial = epoch.current;
    inFlight.current = true;
    setBusy(true);
    setIssue("");
    try {
      const file = edition.files.find((candidate) => candidate.assetId === assetId)!;
      const expected = `/api/v1/books/${encodeURIComponent(book.id)}/engine-editions/${encodeURIComponent(edition.proofId)}/files/${assetId}`;
      if (file.ticketPath !== expected) throw Error("Private file scope changed.");
      const result = customerEngineFileTicketV1Schema.parse(
        (await apiRequest<{ data: unknown }>(expected.replace(/^\/api\/v1/u, ""), token)).data,
      );
      if (
        result.bookId !== book.id ||
        result.proofId !== edition.proofId ||
        result.assetId !== assetId ||
        result.checksumSha256 !== file.checksumSha256 ||
        new URL(result.downloadUrl).protocol !== "https:"
      )
        throw Error("Private file identity changed.");
      if (active === context.current && serial === epoch.current)
        setTickets((previous) => ({
          ...previous,
          [assetId]: { url: result.downloadUrl, expiresAt: Date.now() + result.expiresIn * 1000 },
        }));
    } catch {
      if (active === context.current && serial === epoch.current)
        setIssue("We couldn’t open that exact file. Try getting a fresh private link.");
    } finally {
      if (serial === epoch.current) {
        inFlight.current = false;
        if (active === context.current) setBusy(false);
      }
    }
  }
  async function prepare() {
    if (!filesHere || !confirmed || opened.length !== 2 || inFlight.current || !state?.proof)
      return;
    const proofId = state.proof.proofId;
    const serial = epoch.current;
    inFlight.current = true;
    setBusy(true);
    setIssue("");
    try {
      const next =
        delivery?.state === "READY_FOR_DELIVERY" || preparationRequested
          ? await loadVariantState(book.id, token, true)
          : await (() => {
              setPreparationRequested(true);
              return sendNativeStudioCommand(book.id, token, {
                action: "PREPARE_DELIVERY",
                proofId,
              });
            })();
      const accepted = await loadAcceptedEdition(book.id, token);
      if (serial !== epoch.current) return;
      setSnapshot({ state: next, edition: accepted });
      setLinksAt(Date.now());
      if (
        !reviewable(next, accepted, book, requestedEdition) ||
        next.edition?.editionId !== state.edition?.editionId
      ) {
        setOpened([]);
        setConfirmed(false);
        setTickets({});
        setDeliveryOpen(false);
        throw Error("The saved files changed. Review the current edition before continuing.");
      }
      setDeliveryOpen(next.delivery?.state === "READY_FOR_DELIVERY");
      if (next.delivery?.state !== "READY_FOR_DELIVERY")
        setIssue(
          "Your edition is kept. Delivery can continue only once its exact print package and ordering are ready.",
        );
    } catch (error) {
      // A refusal for good says why (A4); anything else may be checked again.
      if (serial === epoch.current)
        setIssue(
          printPreparationRefusal(error) ??
            "We couldn’t confirm delivery preparation. Check the saved files again; no order is claimed here.",
        );
    } finally {
      inFlight.current = false;
      if (serial === epoch.current) setBusy(false);
    }
  }
  /**
   * The approved proof's links (A6) expired before they were opened: sign them again from the
   * same approved proof, keeping what was already checked, and ask for the file to be opened again.
   */
  async function renewLegacyLinks() {
    if (!legacy || !snapshot || inFlight.current) return;
    const serial = epoch.current;
    const before = snapshot;
    inFlight.current = true;
    setBusy(true);
    try {
      const next = await loadVariantState(book.id, token, true);
      if (serial !== epoch.current) return;
      if (
        !legacyNativeReview(next, before.edition, book, requestedEdition) ||
        next.edition?.editionId !== before.state.edition?.editionId ||
        next.proof?.proofId !== before.state.proof?.proofId ||
        next.proof?.cover.checksumSha256 !== before.state.proof?.cover.checksumSha256 ||
        next.proof?.interior.checksumSha256 !== before.state.proof?.interior.checksumSha256
      ) {
        setAttempt((value) => value + 1);
        return;
      }
      setSnapshot({ state: next, edition: before.edition });
      setLinksAt(Date.now());
      setIssue("That private link had expired. Here’s a fresh one: open the file again.");
    } catch {
      if (serial === epoch.current)
        setIssue("We couldn’t get a fresh private link. Check saved files again to try once more.");
    } finally {
      inFlight.current = false;
      if (serial === epoch.current) setBusy(false);
    }
  }
  // An order placed here is recorded on the book, as the guided steps do, so the book shows its
  // order and locks against a new proof while Gelato prints this one (audit 2026-11-01).
  async function recordOrder(physicalOrderId: string) {
    const proofId = snapshot?.state.proof?.proofId;
    if (!proofId) return;
    try {
      await sendNativeStudioCommand(book.id, token, {
        action: "RECORD_PRINT_ORDER",
        proofId,
        physicalOrderId,
      });
      setUnrecordedOrder(undefined);
      setAttempt((value) => value + 1);
    } catch {
      // Recording the same order again is safe (the server returns early), so keep it to retry.
      setUnrecordedOrder(physicalOrderId);
      setIssue(
        "Your order was sent. We couldn’t mark this book as ordered yet, so it isn’t locked against changes. Try marking it again.",
      );
    }
  }
  // Where delivery stands when there is nothing to prepare: said first, with no file confirmation.
  // A book that changed after approval still has something to do, so it never hears "nothing more".
  const deliveryNote =
    delivery?.state === "AWAITING_PROVIDER_ACTIVATION" ? (
      filesHere || unconfirmed ? (
        <p>{PRINTING_NOT_OPEN_NOTHING_TO_DO} Your saved edition is safe to read in My Library.</p>
      ) : (
        <p>{PRINTING_NOT_OPEN}</p>
      )
    ) : delivery?.state === "NO_PRINTED_COPY" ? (
      <p>{NO_PRINTED_COPY}</p>
    ) : delivery?.state === "NOT_PRINTABLE" ? (
      <p>This edition does not have an available print format.</p>
    ) : delivery?.state === "ORDERED" && delivery.physicalOrderId ? (
      <Link to={orderHref(delivery.physicalOrderId, page, readingSelection(query))}>
        View your existing order
      </Link>
    ) : null;
  return (
    <PrintShell title="Print review" back={back}>
      <header className="print-heading">
        <h1>One last look at your book.</h1>
        <p>
          {filesHere && deliveryNote
            ? "Your digital approval is saved for the exact edition you kept."
            : "Open the cover and every inside page of the exact edition you kept. Choosing delivery is a separate step."}
        </p>
      </header>
      <div className="print-layout">
        <PrintBookSummary
          book={book}
          edition="Kept digital edition"
          facts={
            filesHere && state?.proof
              ? [{ label: "Interior", value: `${state.proof.pageCount} pages` }]
              : []
          }
          readTo={back.to}
        >
          {filesHere && (
            <details className="print-version">
              <summary>Book files and version</summary>
              <p>{state?.edition?.editionId}</p>
              <p>
                Paper finish and binding are not confirmed here. These digital files alone are not
                permission to print.
              </p>
            </details>
          )}
        </PrintBookSummary>
        <section className="print-task" aria-label="Print review and delivery">
          {issue && <p role="status">{issue}</p>}
          {unrecordedOrder && (
            <Button tone="secondary" onClick={() => void recordOrder(unrecordedOrder)}>
              Try marking it again
            </Button>
          )}
          {checking ? (
            <p role="status">Checking the exact saved edition…</p>
          ) : unconfirmed && !legacy ? (
            // Approved before retained captures, or a check this capture can't make: the approval
            // stands and nothing is substituted. Never a Studio loop or a second approval route.
            <>
              <h2>Your approval is saved.</h2>
              <p>
                We can’t confirm this edition’s print files from this page yet. Nothing was changed
                or substituted.
              </p>
              {deliveryNote}
              {/* Native books don't use the established print package, so help is the way on. */}
              <Link to={printHelpHref(book.id, { page })}>Ask us about printing this book</Link>
            </>
          ) : !filesHere ? (
            <>
              <h2>These files need another look.</h2>
              <p>
                This link cannot confirm the current approved files. We haven’t substituted a
                different edition or changed your approval.
              </p>
              {deliveryNote}
              <Link to={`/app/books/${encodeURIComponent(book.id)}/studio/review`}>
                Return to Studio
              </Link>
            </>
          ) : (
            <>
              <h2>Your saved files</h2>
              {deliveryNote}
              <p>
                {deliveryNote
                  ? "Your digital approval is already saved. You can still open its exact files here; this does not place an order."
                  : "Your digital approval is already saved. The checks below are for this visit; they do not create another approval or place an order."}
              </p>
              {legacy && !deliveryNote && (
                // Approved before we kept a record of its print files (A6): the edition the owner
                // approved is prepared for print again, from its own files.
                <p>
                  You approved this book before we kept a record of its print files, so they need
                  preparing again. Open the cover and inside pages of the edition you approved, then
                  prepare them. Nothing is printed until you confirm delivery.
                </p>
              )}
              {(["COVER", "INTERIOR"] as const).map((assetId) => (
                <div className="print-actions" key={assetId}>
                  {legacy ? null : (
                    <Button
                      tone="secondary"
                      disabled={busy}
                      onClick={() => void privateFile(assetId)}
                    >
                      Get private {assetId === "COVER" ? "cover" : "inside-page"} link
                    </Button>
                  )}
                  {(legacy
                    ? state?.proofUrls
                    : tickets[assetId] && tickets[assetId]!.expiresAt > Date.now()) && (
                    <a
                      href={
                        legacy
                          ? state!.proofUrls![assetId === "COVER" ? "cover" : "interior"]
                          : tickets[assetId]!.url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        const url =
                          state?.proofUrls?.[assetId === "COVER" ? "cover" : "interior"] ?? "";
                        // An expired link opens nothing: it is signed again, never counted.
                        if (legacy && Date.now() >= signedLinkExpiresAt(url, linksAt) - 15_000) {
                          event.preventDefault();
                          void renewLegacyLinks();
                          return;
                        }
                        setOpened((previous) => [...new Set([...previous, assetId])]);
                        setConfirmed(false);
                      }}
                    >
                      Open {assetId === "COVER" ? "cover" : "inside pages"} PDF
                    </a>
                  )}
                </div>
              ))}
              {!deliveryNote && (
                <>
                  <Checkbox
                    label="I’ve checked both files and want to use this exact edition"
                    checked={confirmed}
                    disabled={opened.length !== 2 || busy}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                  <Button
                    busy={busy}
                    disabled={busy || !confirmed || opened.length !== 2}
                    onClick={() => void prepare()}
                  >
                    {delivery?.state === "READY_FOR_DELIVERY"
                      ? "Use these files for delivery"
                      : preparationRequested
                        ? "Check print preparation"
                        : "Prepare these print files"}
                  </Button>
                </>
              )}
              {deliveryOpen &&
                confirmed &&
                delivery?.state === "READY_FOR_DELIVERY" &&
                delivery.entitlementId && (
                  <PrintOrderJourney
                    key={identity}
                    bookId={book.id}
                    bookRevision={delivery.bookRevision}
                    previewId={delivery.previewId}
                    entitlementId={delivery.entitlementId}
                    idToken={token}
                    bookTitle={book.title}
                    returnTo={back}
                    readingPageNumber={page}
                    {...(readingSelection(query)
                      ? { readingEdition: readingSelection(query)! }
                      : {})}
                    onOrderSent={(physicalOrderId) => void recordOrder(physicalOrderId)}
                  />
                )}
            </>
          )}
          <Button tone="quiet" disabled={busy} onClick={() => setAttempt((value) => value + 1)}>
            Check saved files again
          </Button>
        </section>
      </div>
    </PrintShell>
  );
}
